// frontend/src/pages/admin/DealerAccessRequestsSection.tsx
// Sales Round B (2026-09-15) — dealer rows on the EXISTING Access Requests page.
// The lead rows below it are untouched. The server decides who may approve
// (admin tier) and scopes non-admins to their own rows; the role check here only
// hides buttons.
import { useCallback, useEffect, useState } from 'react';
import { Building2, Check, X, AlertTriangle } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { categoryLabel } from '../dealers/DealerAssignments';

interface PersonRef { id: string; name: string }
export interface DealerAccessRequestRow {
  id: string;
  dealerName: string;
  categories: string[];
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  note: string | null;
  decisionNote: string | null;
  createdAt: string;
  grantedCategories: string[];
  refusedCategories: string[];
  requestedBy: PersonRef | null;
  decidedBy: PersonRef | null;
  ownerName?: string | null;
  conflicts?: Array<{ category: string; holderName: string }>;
}

const ADMIN_TIER = new Set(['owner', 'admin', 'super_admin', 'operations_manager']);

export function DealerAccessRequestsSection({ status }: { status: string }) {
  const { user } = useAuthStore();
  const canDecide = ADMIN_TIER.has((user?.role?.name ?? '').trim().toLowerCase());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DealerAccessRequestRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status.toUpperCase();
      const res = await api.get('/dealer-access-requests', { params });
      if (!Array.isArray(res.data?.data?.requests)) throw new Error('Invalid request list');
      setRows(res.data.data.requests);
    } catch {
      setRows([]);
      setError('Could not load dealer access requests.');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (row: DealerAccessRequestRow, action: 'approve' | 'reject') => {
    let decisionNote: string | undefined;
    if (action === 'reject') {
      const n = window.prompt('Why are you rejecting this request? The requester sees this.');
      if (n === null) return;
      decisionNote = n.trim() || undefined;
    }
    setBusy(row.id);
    setMessage(null);
    try {
      const res = await api.patch(`/dealer-access-requests/${row.id}`, { action, ...(decisionNote ? { decisionNote } : {}) });
      const d = res.data?.data;
      if (action === 'approve') {
        const granted = (d?.grantedCategories ?? []).map(categoryLabel).join(', ');
        const refused = (d?.refusedCategories ?? []).map(categoryLabel).join(', ');
        setMessage(refused ? `Approved ${granted}. Refused ${refused}.` : `Approved ${granted}.`);
      } else {
        setMessage('Request rejected.');
      }
      await load();
    } catch (err: any) {
      setMessage(err?.response?.data?.error ?? 'Could not save the decision. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p role="status">Loading dealer access requests…</p>;
  if (error) return <div role="alert">{error} <button onClick={load}>Retry</button></div>;
  if (rows.length === 0 && !message) return null;

  return (
    <section data-testid="dealer-access-requests" className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Building2 size={18} className="text-[#1F4B3A]" />
        <h2 className="text-lg font-semibold text-gray-900">Dealer access requests</h2>
      </div>
      {message && (
        <p className="mb-3 flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={14} /> {message}
        </p>
      )}
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="flex flex-col gap-3 rounded-lg border border-gray-100 p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-900">{row.dealerName}</h3>
              <p className="mt-0.5 text-sm text-gray-600">Requested by {row.requestedBy?.name ?? 'Unknown user'}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {row.categories.map((c) => (
                  <span key={c} className="rounded-full bg-[#1F4B3A]/10 px-2 py-0.5 text-xs font-medium text-[#1F4B3A]">{categoryLabel(c)}</span>
                ))}
              </div>
              {/* 🔒 A1 — "No owner" read like a gap in the data. A dealer
                  nobody holds belongs to the company on purpose, and the
                  approver deciding this request needs the difference. */}
              {row.ownerName !== undefined && (
                <p className="mt-2 text-xs text-gray-500">{row.ownerName ? `Owner: ${row.ownerName}` : 'Company dealer — no owner'}</p>
              )}
              {/* 🔒 Spec A5 — approving MOVES the category off its holder; the
                  holder is told, not asked. This line used to say it "will be
                  refused", which is what the server did BEFORE
                  `reassignCategories`: an approver read it, pressed Approve
                  believing nothing moved, and the holder lost the category
                  with nobody having told him. The sentence names the person
                  who loses it and the person who gets it. */}
              {(row.conflicts ?? []).map((c) => (
                <p
                  key={c.category}
                  data-testid={`conflict-${row.id}-${c.category}`}
                  className="mt-1 text-xs font-medium text-amber-700"
                >
                  {categoryLabel(c.category)} is already held by {c.holderName} — approving moves it from{' '}
                  {c.holderName} to {row.requestedBy?.name ?? 'the requester'}.
                </p>
              ))}
              {/* 🔒 Spec A6 — the other half of the picture. A requested
                  category that nobody holds is a COMPANY category, and
                  approving it takes nothing from anyone. Without this line the
                  approver has to infer that from the absence of a warning,
                  which is exactly the reading that makes a company dealer look
                  like an oversight instead of a decision.
                  `conflicts` is admin-only on the wire, so the line is drawn
                  only when it was sent; a non-admin row says nothing rather
                  than claiming every category is free. */}
              {row.conflicts !== undefined && (() => {
                const held = new Set(row.conflicts!.map((c) => c.category));
                const free = row.categories.filter((c) => !held.has(c));
                if (free.length === 0) return null;
                const names = free.map(categoryLabel);
                const list = names.length > 1
                  ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
                  : names[0];
                return (
                  <p data-testid={`free-note-${row.id}`} className="mt-1 text-xs font-medium text-emerald-700">
                    {list} {free.length === 1 ? 'is' : 'are'} held by nobody — approving{' '}
                    {free.length === 1 ? 'it' : 'them'} takes nothing from anyone.
                  </p>
                );
              })()}
              {row.note && <p className="mt-2 text-sm text-gray-600">Note: {row.note}</p>}
              {row.status !== 'PENDING' && (
                <p className="mt-2 text-sm text-gray-700">
                  {row.status === 'APPROVED' ? 'Approved' : 'Rejected'}
                  {row.decidedBy ? ` by ${row.decidedBy.name}` : ''}
                  {row.decisionNote ? ` — ${row.decisionNote}` : ''}
                </p>
              )}
            </div>
            {canDecide && row.status === 'PENDING' && (
              <div className="flex shrink-0 flex-row gap-2 sm:flex-col">
                {/* 🔒 The button says what it TAKES. A decision that moves a
                    category off a named manager must not read the same as one
                    that costs nobody anything. `conflicts` is admin-only on the
                    wire; when it was not sent the button stays plain rather
                    than claiming a move it cannot see. */}
                <button
                  onClick={() => decide(row, 'approve')}
                  disabled={busy === row.id}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <Check size={14} />
                  {(row.conflicts?.length ?? 0) > 0
                    ? `Approve & move ${row.conflicts!.length} ${row.conflicts!.length === 1 ? 'category' : 'categories'}`
                    : 'Approve'}
                </button>
                <button
                  onClick={() => decide(row, 'reject')}
                  disabled={busy === row.id}
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
                >
                  <X size={14} /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
